import { useEffect, useMemo } from "react";
import { BUILDINGS } from "../content/world";
import { SETTLEMENTS } from "../content/core";
import type { GameState, PanelId, SettlementId } from "../game/types";
import { ActionPixelIcon, InteriorScene } from "../render/interiorSprites";

export type InteriorTarget =
  | { kind: "building"; uid: string }
  | { kind: "service"; name: string; settlement: SettlementId };

type Action = { label: string; icon: string; panel?: Exclude<PanelId, null>; special?: "sleep" | "charter" | "mine" };

function titleCase(value: string): string {
  return value.replace(/(^|[-\s])\w/g, (letter) => letter.toUpperCase());
}

function actionsForBuilding(buildingId: string): Action[] {
  const def = BUILDINGS[buildingId];
  const tags = new Set(def.tags);
  const actions: Action[] = [];
  if (tags.has("bed") || buildingId === "cabin") actions.push({ label: "Sleep until morning", icon: "☾", special: "sleep" });
  if (tags.has("storage") || tags.has("trade")) actions.push({ label: "Open storage & market", icon: "▣", panel: "inventory" });
  if (tags.has("craft") || tags.has("forge") || tags.has("workshop")) actions.push({ label: "Use crafting stations", icon: "⚒", panel: "crafting" });
  if (tags.has("animal") || tags.has("pet") || tags.has("coop")) actions.push({ label: "Manage animals & pets", icon: "◉", panel: "pets" });
  if (tags.has("housing")) actions.push({ label: "Visit residents", icon: "♟", panel: "social" });
  if (tags.has("worker") || tags.has("contracts")) actions.push({ label: "Manage workers & charter", icon: "⌘", special: "charter" });
  if (tags.has("travel")) actions.push({ label: "Plan a journey", icon: "⌖", panel: "map" });
  if (tags.has("weather") || tags.has("magic")) actions.push({ label: "Study the seasonal codex", icon: "✦", panel: "codex" });
  if (actions.length === 0) actions.push({ label: "Inspect the room", icon: "◇", panel: "codex" });
  return actions;
}

function actionsForService(name: string): Action[] {
  const value = name.toLowerCase();
  const actions: Action[] = [];
  if (/shop|market|exchange|auction/.test(value)) actions.push({ label: "Browse goods", icon: "◇", panel: "inventory" });
  if (/carpenter/.test(value)) actions.push({ label: "Plan construction", icon: "⌂", panel: "building" });
  if (/animal|pet|conservatory/.test(value)) actions.push({ label: "View animals & companions", icon: "◉", panel: "pets" });
  if (/blacksmith|mechanist|alchemist|academy|forge/.test(value)) actions.push({ label: "Use specialist crafting", icon: "⚒", panel: "crafting" });
  if (/guild|board|hall|archive|loom/.test(value)) actions.push({ label: "Speak with local residents", icon: "♟", panel: "social" });
  if (/archive|academy|loom/.test(value)) actions.push({ label: "Open the codex", icon: "▤", panel: "codex" });
  if (/lift/.test(value)) actions.push({ label: "Enter the mine", icon: "⛏", special: "mine" });
  if (actions.length === 0) actions.push({ label: "Meet the locals", icon: "♟", panel: "social" });
  return actions;
}

export function BuildingInterior({ state, target, onExit, onPanel, onSleep, onCharter, onEnterMine }: {
  state: GameState;
  target: InteriorTarget;
  onExit: () => void;
  onPanel: (panel: Exclude<PanelId, null>) => void;
  onSleep: () => void;
  onCharter: () => void;
  onEnterMine: () => void;
}) {
  const details = useMemo(() => {
    if (target.kind === "building") {
      const placed = state.buildings.find((entry) => entry.uid === target.uid);
      const def = placed ? BUILDINGS[placed.buildingId] : BUILDINGS.cabin;
      return {
        name: def.name,
        subtitle: `Lantern Acre · Tier ${placed?.tier ?? 1}`,
        description: def.description,
        palette: def.palette,
        tags: def.tags,
        actions: actionsForBuilding(def.id),
        buildingId: def.id,
        service: undefined,
        settlementId: "lantern-acre" as SettlementId,
        tier: placed?.tier ?? 1,
      };
    }
    const settlement = SETTLEMENTS[target.settlement];
    return {
      name: titleCase(target.name),
      subtitle: `${settlement.name} · ${settlement.category}`,
      description: `A working ${target.name} serving the people of ${settlement.name}.` ,
      palette: [settlement.palette[0], settlement.palette[1], settlement.palette[2]] as [string, string, string],
      tags: settlement.specialty,
      actions: actionsForService(target.name),
      buildingId: undefined,
      service: target.name,
      settlementId: target.settlement,
      tier: 1,
    };
  }, [state.buildings, target]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onExit(); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onExit]);

  const activate = (action: Action) => {
    if (action.panel) onPanel(action.panel);
    if (action.special === "sleep") onSleep();
    if (action.special === "charter") onCharter();
    if (action.special === "mine") onEnterMine();
  };

  return <section className="building-interior" aria-label={`${details.name} interior`} style={{ "--inside-wall": details.palette[0], "--inside-accent": details.palette[1], "--inside-dark": details.palette[2] } as React.CSSProperties}>
    <header className="interior-header">
      <div><span>{details.subtitle}</span><h1>{details.name}</h1></div>
      <button className="interior-exit top" onClick={onExit} aria-label="Exit building">Exit building ×</button>
    </header>
    <div className="interior-room"><InteriorScene buildingId={details.buildingId} service={details.service} settlementId={details.settlementId} tier={details.tier} /></div>
    <aside className="interior-panel">
      <span className="eyebrow">INSIDE</span>
      <h2>{details.name}</h2>
      <p>{details.description}</p>
      <div className="tag-row">{details.tags.map((tag) => <small key={tag}>{tag}</small>)}</div>
      <div className="interior-actions">{details.actions.map((action) => <button key={action.label} onClick={() => activate(action)}><span><ActionPixelIcon label={action.label} /></span><b>{action.label}</b></button>)}</div>
      <button className="interior-exit bottom" onClick={onExit}>Return outside</button>
    </aside>
  </section>;
}
